import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatDay, formatPercent } from '~/lib/format';
import { candles } from '~data';
import { BASIS_NOTE, valueSeries, type Basis } from '~/stores/series-store';
import { Swatch } from '~/features/swatch';

// Several funds on one plot.
//
// Indexed to 100 at a shared start rather than drawn in dollars. Two things
// force it: the funds trade at different prices, so raw closes would space the
// lines by unit price rather than by anything you care about; and they list on
// different dates, so a fund that has simply existed longer would appear to have
// outperformed. The base is the first session every selected fund has a close
// for, which is the only window in which the comparison means anything — and it
// is stated under the chart rather than left to be inferred.
//
// One axis, always. The alternative to indexing is a second y-scale, which
// invents a correlation out of where two arbitrary scales happen to line up.
const VB_W = 100;
const VB_H = 40;
const PAD = 2;
const PAD_X = 3;

interface Point {
    t: number;
    value: number;
}

interface Series {
    symbol: string;
    color: string;
    dash?: string;
    points: Point[];
}

// Colours are given, not derived. The caller owns which fund is which hue and
// holds it steady across additions and removals — see compare-store, which also
// owns the dash pattern that distinguishes a ninth fund from the first.
export const OverlayChart: Component<{ series: { symbol: string; color: string; dash?: string }[]; basis: Basis; height?: string }> = (props) => {
    // The first session every selected fund has a close for.
    const start = createMemo<number | null>(() => {
        const firsts = props.series
            .map((s) => candles(s.symbol)[0]?.date)
            .filter((d): d is string => d != null)
            .map((d) => Date.parse(d));
        return firsts.length === props.series.length && firsts.length > 0 ? Math.max(...firsts) : null;
    });

    const series = createMemo<Series[]>(() => {
        const from = start();
        if (from == null) return [];

        return props.series.map(({ symbol, color, dash }) => {
            const inWindow = valueSeries(symbol, props.basis, from);
            const base = inWindow[0]?.value ?? 0;
            return {
                symbol,
                color,
                dash,
                points: base > 0 ? inWindow.map((p) => ({ t: p.t, value: (p.value / base) * 100 })) : [],
            };
        });
    });

    const span = createMemo(() => {
        const all = series().flatMap((s) => s.points);
        if (all.length === 0) return null;
        const t0 = Math.min(...all.map((p) => p.t));
        const t1 = Math.max(...all.map((p) => p.t));
        const lo = Math.min(...all.map((p) => p.value));
        const hi = Math.max(...all.map((p) => p.value));
        return { t0, t1, tSpan: t1 - t0 || 1, lo, hi, vSpan: hi - lo || 1 };
    });

    // x is time, not sample index — the funds have different bar counts, and
    // plotting by index would slide them out of step with each other.
    const x = (t: number): number => PAD_X + ((t - span()!.t0) / span()!.tSpan) * (VB_W - PAD_X * 2);
    const y = (v: number): number => VB_H - PAD - ((v - span()!.lo) / span()!.vSpan) * (VB_H - PAD * 2);

    const path = (s: Series): string => s.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(3)} ${y(p.value).toFixed(3)}`).join(' ');

    const [hoverT, setHoverT] = createSignal<number | null>(null);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width <= 0 || !span()) return;
        // Inverts x() — see the same note in close-chart.tsx. Taking the fraction
        // over the whole box ignores the PAD_X inset and slides the crosshair
        // away from the cursor by up to that much at either end.
        const across = ((e.clientX - rect.left) / rect.width) * VB_W;
        const fraction = Math.min(1, Math.max(0, (across - PAD_X) / (VB_W - PAD_X * 2)));
        setHoverT(span()!.t0 + fraction * span()!.tSpan);
        setCursor({ x: e.clientX, y: e.clientY });
    };

    /** The last sample at or before the crosshair — each series read at the same
        moment, which is the only way the readout compares like with like. */
    const at = (s: Series): Point | null => {
        const t = hoverT();
        if (t == null) return null;
        let found: Point | null = null;
        for (const p of s.points) {
            if (p.t > t) break;
            found = p;
        }
        return found ?? s.points[0] ?? null;
    };

    return (
        <Show when={span()} fallback={<Text tone='muted' class='block px-2 py-6 text-center'>Not enough shared price history to overlay these</Text>}>
            <div class={`relative w-full ${props.height ?? 'h-56'}`} onMouseMove={track} onMouseLeave={() => setHoverT(null)}>
                <svg class='h-full w-full' viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio='none' fill='none'>
                    {/* 100 is where every line starts, so the level itself is the
                        break-even mark — worth one hairline and no more. */}
                    <line x1={PAD_X} y1={y(100)} x2={VB_W - PAD_X} y2={y(100)} class='stroke-muted' stroke-width='1' vector-effect='non-scaling-stroke' />
                    <For each={series()}>
                        {/* The dash is in screen pixels, which is what
                            non-scaling-stroke buys: the viewBox is 100x40 and
                            stretched to fit, so a dash in user units would come
                            out a different length horizontally than vertically. */}
                        {(s) => (
                            <path
                                d={path(s)}
                                style={{ stroke: s.color }}
                                stroke-width='2'
                                stroke-dasharray={s.dash}
                                stroke-linejoin='round'
                                stroke-linecap={s.dash ? 'butt' : 'round'}
                                vector-effect='non-scaling-stroke'
                            />
                        )}
                    </For>
                    <Show when={hoverT() != null}>
                        <line x1={x(hoverT()!)} y1='0' x2={x(hoverT()!)} y2={VB_H} class='stroke-muted-foreground' stroke-width='1' vector-effect='non-scaling-stroke' />
                    </Show>
                </svg>

                {/* The base, as a corner annotation rather than a footer row.
                    Every line starts at 100 on a shared date, and a reader who
                    does not know which date is reading a chart that means
                    nothing. Hidden while hovering so it never competes with the
                    tooltip, which carries the date itself. */}
                <Show when={hoverT() == null}>
                    <div class='pointer-events-none absolute top-0 left-0 px-2 py-1 whitespace-nowrap'>
                        <Text tone='muted'>
                            100 = {formatDay(new Date(span()!.t0).toISOString().slice(0, 10))} · {BASIS_NOTE[props.basis]}
                        </Text>
                    </div>
                </Show>

                {/* Direct labels at each line's end. Required, not decorative: the
                    palette's worst adjacent pair sits in the separation band that
                    is only legal with a second encoding. */}
                <For each={series()}>
                    {(s) => (
                        <Show when={s.points.length > 0}>
                            <div
                                class='pointer-events-none absolute -translate-y-1/2 pl-1 text-xs'
                                style={{ left: `${(x(s.points[s.points.length - 1].t) / VB_W) * 100}%`, top: `${(y(s.points[s.points.length - 1].value) / VB_H) * 100}%`, color: s.color }}>
                                {s.symbol}
                            </div>
                        </Show>
                    )}
                </For>

                {/* Every fund at the one date, ranked. Which is ahead at a given
                    moment is the whole question this chart is asked, and reading
                    it off a row at the bottom of the pane while looking at the
                    middle of the plot does not work.

                    Portalled and fixed: Panel is overflow-hidden and would clip a
                    tooltip drawn inside the plot. */}
                <Show when={hoverT() != null}>
                    <Portal>
                        <div
                            class='pointer-events-none fixed z-[200] text-foreground rounded border bg-background px-2 py-1 shadow'
                            style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 220)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 40 - series().length * 22)}px` }}>
                            <Text tone='muted' class='block whitespace-nowrap'>
                                {formatDay(new Date(hoverT()!).toISOString().slice(0, 10))}
                            </Text>
                            <For each={[...series()].sort((a, b) => (at(b)?.value ?? 0) - (at(a)?.value ?? 0))}>
                                {(s) => (
                                    <div class='flex items-center justify-between gap-3 whitespace-nowrap'>
                                        <span class='flex items-center gap-1'>
                                            <Swatch color={s.color} dash={s.dash} />
                                            <Text weight='medium'>{s.symbol}</Text>
                                        </span>
                                        <Text tone={(at(s)?.value ?? 100) >= 100 ? 'green' : 'red'}>{formatPercent((at(s)?.value ?? 100) - 100, 1)}</Text>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Portal>
                </Show>
            </div>

        </Show>
    );
};

export default OverlayChart;
